/**
 * Capacity Inputs screen (task 13.1, R3, R7.3, R7.7, R7.8).
 *
 * The Capacity_Period register: headcount, workdays, OOF days, productive hours
 * per workday and availability factor, per FF role and Region, with the derived
 * Capacity_Hours alongside so the arithmetic of R3.3 is visible rather than
 * implied.
 *
 * ## Layout
 *
 * Two columns (UI spec §5.3): the register on the left, the planning assumptions
 * that every figure in it was calculated against on the right, with the capacity
 * formula and its worked example beneath both. The assumptions sit beside the
 * numbers rather than behind a link because they are what makes the numbers
 * checkable — a Capacity_Hours figure with no stated productive-hours rate is not a
 * figure anyone can challenge.
 *
 * ## Editing, and what "a version" means here
 *
 * A row's figures can be adjusted (R3.3): "Edit" opens the panel on that period
 * with its scope locked, and saving records the change as the next version of
 * that scope while retaining the previous ones unchanged. No stored row is ever
 * updated, which is why the table shows the version number and the active flag,
 * and filters to the current version per scope by default so the register reads
 * as "current capacity" rather than as an audit trail.
 *
 * "Copy to a new period" is the other half of that: it seeds the panel with a
 * row's figures but leaves the scope editable, for recording the next period
 * without retyping numbers that have not changed. The two actions differ in
 * exactly one respect — whether the scope is the record's or the writer's — which
 * is what keeps an edit from silently becoming a duplicate under a different
 * scope.
 *
 * Capability: `manpower:read` to view, `capacity:write` to edit (R3.1, R3.7,
 * R7.3, R7.8). Read gating renders a notice; it does not return before the hooks.
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CapacityPeriod } from '@complan/contracts';
import { usePageChrome } from '@/components/layout/page-chrome';
import { hasCapability, useSession } from '@/app/session/session';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CapacityStateBadge } from '@/components/ui/capacity-state-badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FilterActions,
  FilterBar,
  FilterCheckbox,
  FilterSelect,
} from '@/shared/components/filter-bar';
import { EditorDialog } from '@/shared/components/EditorDialog';
import { useManpowerReferenceSet, toNameLookup } from './manpower-reference-data';
import { findingsFromError } from '@/features/admin/admin-findings';
import {
  useCapacityPeriods,
  useCreateCapacityPeriod,
  useEditCapacityPeriod,
} from './capacity-inputs/capacity-inputs-data';
import {
  CapacityPeriodPanel,
  capacityEditorNoteKey,
  capacityEditorTitleKey,
  type CapacityPeriodPanelMode,
} from './capacity-inputs/CapacityPeriodPanel';
import { usePlanningAssumptions } from './capacity-inputs/planning-assumptions-data';
import { CapacityFormulaBlock } from './capacity-inputs/CapacityFormulaBlock';
import { PlanningAssumptionsCard } from './capacity-inputs/PlanningAssumptionsCard';
import { formatHours } from './shared/manpower-format';
import { periodLabelOfRange, periodText } from './shared/period-label';
import { RoleBadge } from './shared/RoleBadge';
import { FF_ROLE_OPTIONS, ffRoleLabel } from './ff-role-options';

export function CapacityInputsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const canRead = hasCapability(session.data, 'manpower:read');
  const canWrite = hasCapability(session.data, 'capacity:write');

  usePageChrome({
    title: t('manpower.capacityInputs.title'),
    subtitle: t('manpower.capacityInputs.subtitle'),
  });

  const [ffRole, setFfRole] = useState<string | undefined>(undefined);
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [showAllVersions, setShowAllVersions] = useState<boolean>(false);

  /**
   * The editor panel's seed. `basedOn` carries the version whose figures pre-fill
   * the form, or `null` for a blank entry; `mode` decides whether that version's
   * scope is the record being edited (`'edit'`) or merely a starting point for a
   * new one (`'create'`).
   *
   * `key` is what remounts the panel: it is bumped on every explicit reseed (a
   * row action, the add control, a completed save) and passed to the panel as its
   * React `key`, so the panel's `useState` initialiser is the only seeding path.
   *
   * The seed also decides the dialog's heading and note, through the editor's own
   * `capacityEditor*Key` helpers — the three cases (add, edit, copy) are one rule
   * held in one place rather than a title here and a heading there.
   */
  const [seed, setSeed] = useState<{
    readonly mode: CapacityPeriodPanelMode;
    readonly basedOn: CapacityPeriod | null;
    readonly key: number;
  }>({ mode: 'create', basedOn: null, key: 0 });

  /**
   * Whether the editor dialog is showing.
   *
   * Separate from {@link seed} because the two answer different questions: the seed
   * is *what* the form holds, and this is whether the reader is looking at it. They
   * were one thing while the editor was a permanent side panel; a dialog has to be
   * closable without discarding the entry behind it.
   */
  const [editorOpen, setEditorOpen] = useState<boolean>(false);

  const capacityQuery = useCapacityPeriods();
  const createPeriod = useCreateCapacityPeriod();
  const editPeriod = useEditCapacityPeriod();
  const assumptionsQuery = usePlanningAssumptions({ enabled: canRead || canWrite });
  const regionsQuery = useManpowerReferenceSet('region', { enabled: canRead || canWrite });
  const regions = regionsQuery.data ?? [];
  const regionNames = useMemo(() => toNameLookup(regionsQuery.data), [regionsQuery.data]);

  // A rejected write returns its findings on the thrown error; the panel renders
  // them and, for the acknowledgeable warnings (R3.6's recalculation notice and
  // the capacity shortfall), offers the confirmation that re-submits. Read from
  // the mutation the current mode submits through, so a stale error from the other
  // one cannot surface against an entry it never described.
  const activeMutation = seed.mode === 'edit' ? editPeriod : createPeriod;
  const findings = findingsFromError(activeMutation.error);

  /**
   * Reseeds the editor and opens it. Both mutations are reset: any findings from a
   * previous rejected submit described the entry being replaced, whichever path
   * produced them.
   */
  const seedEditor = useCallback(
    (basedOn: CapacityPeriod | null, mode: CapacityPeriodPanelMode = 'create') => {
      createPeriod.reset();
      editPeriod.reset();
      setSeed((previous) => ({ mode, basedOn, key: previous.key + 1 }));
      setEditorOpen(true);
    },
    [createPeriod, editPeriod],
  );

  /** Closes the editor, leaving no findings behind to surface against the next entry. */
  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    createPeriod.reset();
    editPeriod.reset();
  }, [createPeriod, editPeriod]);

  // Filtering is client-side: the endpoint is unpaged at Manpower's volumetric
  // baseline (R9.3), so a filter change needs no round trip.
  //
  // "Current" is the highest version of a scope, not `isActive`. Creating a new
  // version does not deactivate its predecessor (`capacity-period-service.ts`
  // only inserts), so every version of a scope comes back active and filtering on
  // `isActive` would show the same scope several times over. Deactivation is a
  // separate action (R3.8) and is reported in its own column.
  const rows = useMemo(() => {
    const items = capacityQuery.data?.items ?? [];
    const currentVersionByScope = new Map<string, number>();
    for (const item of items) {
      const key = scopeKeyOf(item);
      currentVersionByScope.set(key, Math.max(currentVersionByScope.get(key) ?? 0, item.version));
    }

    return items
      .filter((item) => showAllVersions || currentVersionByScope.get(scopeKeyOf(item)) === item.version)
      .filter((item) => (ffRole === undefined ? true : item.ffRole === ffRole))
      .filter((item) => (regionId === undefined ? true : item.regionId === regionId))
      .map((item) => ({
        period: item,
        isCurrent: currentVersionByScope.get(scopeKeyOf(item)) === item.version,
      }))
      .sort((a, b) => compareCapacityPeriods(a.period, b.period));
  }, [capacityQuery.data, ffRole, regionId, showAllVersions]);

  /**
   * The row the capacity formula works through: the largest complete current period.
   *
   * The largest so the example is a figure worth checking rather than an edge case,
   * and complete because a period with no agreed headcount has no capacity to
   * demonstrate. `undefined` when the register holds nothing usable, in which case
   * the formula block simply omits the example rather than inventing numbers.
   */
  const formulaExample = useMemo(() => {
    const candidates = rows
      .filter((row) => row.isCurrent && row.period.headcount !== null)
      .map((row) => ({ row, capacityHours: deriveCapacityHours(row.period) }))
      .filter((entry): entry is { row: (typeof rows)[number]; capacityHours: number } =>
        entry.capacityHours !== null,
      )
      .sort((a, b) => b.capacityHours - a.capacityHours);

    const best = candidates[0];
    if (best === undefined) {
      return undefined;
    }
    const period = best.row.period;
    return {
      roleLabel: ffRoleLabel(period.ffRole),
      periodLabel: periodText(
        t,
        periodLabelOfRange(period.effectivePeriodStart, period.effectivePeriodEnd),
        'full',
      ),
      headcount: period.headcount ?? 0,
      availableDays: period.workdays - period.oofDays,
      productiveHoursPerWorkday: Number(period.productiveHoursPerWorkday),
      availabilityFactor: Number(period.availabilityFactor),
      capacityHours: best.capacityHours,
    };
  }, [rows, t]);

  if (session.isSuccess && !canRead && !canWrite) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.capacityInputs.noAccess')}
        </p>
      </div>
    );
  }

  const columnCount = canWrite ? 9 : 8;

  return (
    <div className="flex flex-col gap-4">
      <FilterBar label={t('manpower.capacityInputs.filterBarLabel')}>
        <FilterSelect
          label={t('manpower.capacityInputs.filterRole')}
          value={ffRole ?? ''}
          onChange={(value) => setFfRole(value === '' ? undefined : value)}
        >
          <option value="">{t('manpower.capacityInputs.allRoles')}</option>
          {FF_ROLE_OPTIONS.map((role) => (
            <option key={role.id} value={role.id}>
              {role.label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t('manpower.capacityInputs.filterRegion')}
          value={regionId ?? ''}
          onChange={(value) => setRegionId(value === '' ? undefined : value)}
        >
          <option value="">{t('manpower.capacityInputs.allRegions')}</option>
          {regions.map((region) => (
            <option key={region.id} value={region.id}>
              {region.name}
            </option>
          ))}
        </FilterSelect>

        <FilterCheckbox
          label={t('manpower.capacityInputs.showAllVersions')}
          checked={showAllVersions}
          onChange={setShowAllVersions}
        />

        {canWrite && (
          <FilterActions>
            {/* Opens the editor on a blank entry: it clears any row seed first, so
                arriving here after a row's "Copy to new period" cannot leave the
                previous row's figures in the form. */}
            <Button
              size="sm"
              onClick={() => seedEditor(null)}
              disabled={regions.length === 0}
              {...(regions.length === 0 && {
                title: t('manpower.capacityInputs.needsRegion'),
              })}
            >
              {t('manpower.capacityInputs.add')}
            </Button>
          </FilterActions>
        )}
      </FilterBar>

      {/* The register and the planning-assumptions card sit side by side (UI spec
          §5.3) in the click dummy's own proportions. `items-start` keeps the
          right-hand card its own height instead of stretching it down a long table;
          the columns stack below `lg`, where a ten-column table needs the full
          width to itself. The editor is not one of these columns — it is the dialog
          at the end of this component. */}
      <div className="grid items-start gap-4 lg:grid-cols-[1.7fr_1fr]">
        <div className="min-w-0">
          {/* The state chain and the table share one card, as on the Initiatives
              list: a load failure, a first load and an empty register are all
              "what this card has to say" rather than three separately framed
              surfaces. */}
          <Card className="gap-2 p-5">
            <p className="text-txt text-[13px] font-semibold">
              {t('manpower.capacityInputs.registerHeading')}
            </p>
            <p className="text-muted-foreground mb-2 text-[11.5px]">
              {t('manpower.capacityInputs.registerSubtitle')}
            </p>
            {capacityQuery.isError ? (
              <Alert variant="destructive">
                <AlertTitle>{t('common.error')}</AlertTitle>
                <AlertDescription>{t('manpower.capacityInputs.loadError')}</AlertDescription>
              </Alert>
            ) : capacityQuery.isPending ? (
              <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
            ) : (
              <Table>
                <caption className="sr-only">{t('manpower.capacityInputs.tableCaption')}</caption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col" className="text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.role')}
                    </TableHead>
                    <TableHead scope="col" className="text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.region')}
                    </TableHead>
                    <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.headcount')}
                    </TableHead>
                    <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.workdays')}
                    </TableHead>
                    <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.oofDays')}
                    </TableHead>
                    {/* Derived, not entered: workdays minus OOF days, shown because it
                        is the quantity the formula actually multiplies and a reader
                        checking a capacity figure otherwise has to compute it. */}
                    <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.availableDays')}
                    </TableHead>
                    <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.capacityHours')}
                    </TableHead>
                    <TableHead scope="col" className="text-[10.5px] uppercase">
                      {t('manpower.capacityInputs.period')}
                    </TableHead>
                    {canWrite && (
                      // The action column's heading is for screen readers only, as
                      // in the export and role-admin tables: a visible "Actions"
                      // label names the buttons twice over.
                      <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                        <span className="sr-only">{t('manpower.capacityInputs.actions')}</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        className="text-muted-foreground py-6 text-center text-[12.3px]"
                      >
                        {t('manpower.capacityInputs.empty')}
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map(({ period, isCurrent }) => {
                    const capacityHours = deriveCapacityHours(period);
                    const availableDays = period.workdays - period.oofDays;
                    // The productive-hours and availability columns are gone from
                    // this table — the assumptions card beside it states the rates —
                    // but a period may legitimately carry its own. Hiding that would
                    // leave a capacity figure no reader could reproduce, so a row
                    // whose rates differ from the assumptions in force says so.
                    const deviates =
                      assumptionsQuery.data !== undefined &&
                      (Number(period.productiveHoursPerWorkday) !==
                        assumptionsQuery.data.productiveHoursPerWorkday ||
                        Number(period.availabilityFactor) !==
                          assumptionsQuery.data.availabilityFactor);
                    return (
                      <TableRow key={period.id}>
                        <TableCell className="text-[12.3px]">
                          <RoleBadge role={period.ffRole} />
                          {!isCurrent && (
                            <span className="text-muted-foreground ml-2 text-[11px]">
                              {t('manpower.capacityInputs.superseded')}
                            </span>
                          )}
                          {!period.isActive && (
                            <span className="text-muted-foreground ml-2 text-[11px]">
                              {t('manpower.capacityInputs.deactivated')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-[12.3px]">
                          {regionNames.get(period.regionId) ?? period.regionId}
                        </TableCell>
                        <TableCell className="text-right text-[12.3px] tabular-nums">
                          {/* An em dash, not a zero: R3.4 makes an absent headcount an
                              unstated figure, and a zero would read as "nobody", which
                              is a different and confidently wrong claim. */}
                          {period.headcount ?? t('manpower.absent')}
                        </TableCell>
                        <TableCell className="text-right text-[12.3px] tabular-nums">
                          {period.workdays}
                        </TableCell>
                        <TableCell className="text-right text-[12.3px] tabular-nums">
                          {period.oofDays}
                        </TableCell>
                        <TableCell className="text-right text-[12.3px] tabular-nums">
                          {availableDays}
                        </TableCell>
                        <TableCell className="text-right text-[12.3px] font-medium tabular-nums">
                          {capacityHours === null ? (
                            // R3.4's Data_Incomplete case, in the same pill every other
                            // Manpower screen uses for a capacity state.
                            <CapacityStateBadge state="data_incomplete" />
                          ) : (
                            <>
                              {t('manpower.hours', { value: formatHours(capacityHours) })}
                              {deviates && (
                                <abbr
                                  className="text-muted-foreground ml-1 cursor-help text-[11px] no-underline"
                                  title={t('manpower.capacityInputs.ownRates', {
                                    hours: period.productiveHoursPerWorkday,
                                    availability: formatPercent(period.availabilityFactor),
                                  })}
                                >
                                  {t('manpower.capacityInputs.ownRatesMarker')}
                                </abbr>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-[12.3px] tabular-nums">
                          {periodText(
                            t,
                            periodLabelOfRange(
                              period.effectivePeriodStart,
                              period.effectivePeriodEnd,
                            ),
                            'full',
                          )}
                          <span className="text-muted-foreground ml-2 text-[11px]">
                            {t('manpower.version', { version: period.version })}
                          </span>
                        </TableCell>
                        {canWrite && (
                          // Offered on the current version of a scope only: editing a
                          // superseded version would record figures the register no
                          // longer shows as current, and copying from one would seed
                          // the editor with numbers already replaced.
                          <TableCell className="text-right">
                            {isCurrent && (
                              <div className="flex flex-wrap items-center justify-end gap-1">
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="outline"
                                  onClick={() => seedEditor(period, 'edit')}
                                >
                                  {/* "Resolve" on a period with no agreed headcount:
                                      the action is the same edit, but what it is for is
                                      supplying a missing figure rather than revising a
                                      stated one, and the register should say which. */}
                                  {period.headcount === null
                                    ? t('manpower.capacityInputs.resolve')
                                    : t('manpower.capacityInputs.override')}
                                </Button>
                                <Button
                                  type="button"
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => seedEditor(period, 'create')}
                                >
                                  {t('manpower.capacityInputs.copyToNewPeriod')}
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>

        <PlanningAssumptionsCard
          assumptions={assumptionsQuery.data}
          isPending={assumptionsQuery.isPending}
          isError={assumptionsQuery.isError}
        />
      </div>

      <CapacityFormulaBlock example={formulaExample} />

      {/* The editor as a dialog, which is what the register's row actions now open.
          Modal on purpose: an "Override" is a decision about one named period, and
          the figures being replaced are the ones in the row behind it — a panel
          alongside invited entering values against a different row than intended. */}
      {canWrite && (
        <EditorDialog
          open={editorOpen}
          onOpenChange={(open) => {
            if (!open) {
              closeEditor();
            }
          }}
          title={t(capacityEditorTitleKey(seed.mode, seed.basedOn))}
          note={t(capacityEditorNoteKey(seed.mode, seed.basedOn))}
          width="max-w-[560px]"
          autoFocusHeading
        >
          <CapacityPeriodPanel
            // The seed key remounts the panel, which is how a reseed reaches the
            // form state (see the panel's own note on why this is not an effect).
            key={seed.key}
            mode={seed.mode}
            basedOn={seed.basedOn}
            regions={regions}
            defaultRegionId={regionId ?? regions[0]?.id}
            defaultFfRole={(ffRole as CapacityPeriod['ffRole']) ?? 'ade'}
            onStartBlank={() => seedEditor(null)}
            onSubmit={(payload) => {
              // A saved entry leaves a blank form behind rather than the values just
              // recorded, so the next period is entered from scratch and no one
              // re-submits the same figures by accident.
              const onSuccess = (): void => {
                seedEditor(null);
              };

              const editing = seed.mode === 'edit' ? seed.basedOn : null;
              if (editing !== null) {
                // The scope is dropped rather than sent: it identifies the record
                // being edited and the server reads it from `:id`, so the panel's
                // copy of it has nowhere to go (see `capacityPeriodEditSchema`).
                editPeriod.mutate(
                  {
                    id: editing.id,
                    payload: {
                      headcount: payload.headcount,
                      workdays: payload.workdays,
                      oofDays: payload.oofDays,
                      productiveHoursPerWorkday: payload.productiveHoursPerWorkday,
                      availabilityFactor: payload.availabilityFactor,
                      changeReason: payload.changeReason,
                      acknowledgedWarningRuleIds: payload.acknowledgedWarningRuleIds,
                    },
                  },
                  { onSuccess },
                );
                return;
              }

              createPeriod.mutate(payload, { onSuccess });
            }}
            isSubmitting={activeMutation.isPending}
            findings={findings}
          />
        </EditorDialog>
      )}
    </div>
  );
}

/**
 * Capacity_Hours for one Capacity_Period (R3.3).
 *
 * `null` when headcount is absent: that is the Data_Incomplete case of R3.4, and
 * a period with no agreed headcount has no capacity rather than zero capacity.
 * The decimal fields arrive as strings (the contracts use a decimal text schema
 * so no precision is lost on the wire), hence the `Number` conversions.
 */
function deriveCapacityHours(period: CapacityPeriod): number | null {
  if (period.headcount === null) {
    return null;
  }
  const productiveDays = period.workdays - period.oofDays;
  return (
    period.headcount *
    productiveDays *
    Number(period.productiveHoursPerWorkday) *
    Number(period.availabilityFactor)
  );
}

/** A `0.80`-style factor rendered as `80%`. */
function formatPercent(factor: string): string {
  return `${(Number(factor) * 100).toFixed(0)}%`;
}

/**
 * The scope a version belongs to, matching what `capacity-period-service.ts`
 * groups versions by: role, Region and the effective period. Two rows sharing
 * this key are versions of the same thing.
 */
function scopeKeyOf(period: CapacityPeriod): string {
  return [
    period.ffRole,
    period.regionId,
    period.effectivePeriodStart,
    period.effectivePeriodEnd,
  ].join('|');
}

/** Role, then Region, then period start, then newest version first. */
function compareCapacityPeriods(a: CapacityPeriod, b: CapacityPeriod): number {
  return (
    a.ffRole.localeCompare(b.ffRole) ||
    a.regionId.localeCompare(b.regionId) ||
    a.effectivePeriodStart.localeCompare(b.effectivePeriodStart) ||
    b.version - a.version
  );
}
